import React, { memo, useMemo, useRef, useEffect } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { faPaste } from '@fortawesome/free-solid-svg-icons'
import isEmpty from 'lodash/isEmpty'
import { JsonView as JsonViewLite, defaultStyles } from 'react-json-view-lite'
import styled from 'styled-components'

import localizeDate from '../../common/utils/localizeDate'
import { Button } from '../../common/components/Button'
import { OcppCommunicationLog } from '../../common/redux/api/web-api'
import { theme } from '../theme'
import { SignInArrowIcon } from '../icons/sign-in-arrow-icon'
import { notifySuccess } from '../../common/features/notificationsManager/Notify'
import safeJsonParse from '../safe-json-parse'
import useText from '../../common/hooks/useText'

import {
    SIcon,
    SJsonCopyButton,
    STable,
    STableActions,
    STableBody,
    STableCell,
    STableContainer,
    STableHead,
    STableRow,
} from './logs-table.styles'

type Column = {
    label: string
    width?: number
    accessor: string
}

export type DataTableProps = {
    columns: Column[]
    logs: Array<OcppCommunicationLog>
    actions?: Array<{
        title: string
        onClick: () => void
    }>
    className?: ClassName
    style?: React.CSSProperties
    loading?: boolean
    onScroll?: React.UIEventHandler<HTMLDivElement>
    onLoadMore?: () => void
}

const ScrollToTopButton = styled(Button)`
    position: absolute;
    bottom: 40px;
    right: 60px;
    padding: 10px;
    border-radius: 50%;

    &:hover svg {
        color: white !important;
    }
`

export const LOGS_NO_OF_ITEMS = 10

const FormattedData = ({ formattedData, showData }: { formattedData: string; showData: boolean }) => (
    <span data-testid="formatted-data">
        {showData ? (
            formattedData
        ) : (
            <SIcon icon={faPaste} onClick={() => navigator.clipboard.writeText(formattedData)} />
        )}
    </span>
)

const JsonView = memo(
    ({ message }: { message: string }) => {
        const text = useText()
        const data = useMemo(() => {
            const data = safeJsonParse<{ data?: string } | null>(message, null)
            return data?.data ? { ...data, data: safeJsonParse(data.data, data.data) } : data
        }, [message])

        return data === null ? (
            <>{message?.replace(/\0.*$/g, '')}</>
        ) : (
            <>
                <SJsonCopyButton title={text('lblCopy')}>
                    <SIcon
                        icon={faPaste}
                        onClick={() => {
                            navigator.clipboard.writeText(message)
                            notifySuccess(text('msgOcppLogsCopy'))
                        }}
                    />
                </SJsonCopyButton>

                <JsonViewLite
                    data={data}
                    style={{
                        ...defaultStyles,
                        container: 'json-view',
                        basicChildStyle: 'json-view__child',
                        label: 'json-view__label',
                        stringValue: 'json-view__stringValue',
                        numberValue: 'json-view__numberValue',
                        booleanValue: 'json-view__booleanValue',
                    }}
                    shouldExpandNode={(level, value) => {
                        if (isEmpty(value)) {
                            return false
                        }
                        if (level === 0 || level >= 2) {
                            return true
                        }
                        return false
                    }}
                />
            </>
        )
    },
    (prevProps, nextProps) => {
        return prevProps.message === nextProps.message
    }
)

export function LogsTable({
    columns,
    logs = [],
    actions,
    className,
    style,
    loading,
    onScroll,
    onLoadMore,
}: DataTableProps): React.ReactElement {
    const observerTarget = useRef(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && typeof onLoadMore === 'function') {
                    onLoadMore()
                }
            },
            { threshold: 1 }
        )

        if (observerTarget.current) {
            observer.observe(observerTarget.current)
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current)
            }
        }
    }, [observerTarget, onLoadMore])

    const containerRef = useRef<HTMLDivElement>(null)

    return (
        <STableContainer className={cn(className)} style={style} onScroll={onScroll} ref={containerRef}>
            <STable>
                <thead>
                    <STableRow>
                        {columns.map((column) => (
                            <STableHead key={column.label} data-testid={column.label} width={column.width}>
                                {column.label}
                            </STableHead>
                        ))}
                    </STableRow>
                </thead>
                <STableBody>
                    {logs.map((log, i) => (
                        <STableRow key={log.timestamp} data-testid={'row-' + i}>
                            {columns.map(({ accessor }) => {
                                const formatCell = () => {
                                    if (accessor === 'timestamp') {
                                        return log.timestamp ? localizeDate(log.timestamp) : ''
                                    }
                                    if (accessor === 'formattedData' && log.formattedData) {
                                        return (
                                            <FormattedData
                                                formattedData={log?.formattedData}
                                                showData={!!log?.showData}
                                            />
                                        )
                                    }
                                    if (accessor === 'message') {
                                        return <JsonView message={log.message || '{}'} />
                                    }

                                    if (accessor === 'action') {
                                        const isOutgoing = log.messageDirection === 'Outgoing'
                                        return (
                                            <span title={log.messageDirection} className="font-normal inline-flex">
                                                <SignInArrowIcon
                                                    color={
                                                        isOutgoing
                                                            ? theme.brandColors.CA_Mid_Blue
                                                            : theme.brandColors.CA_Green
                                                    }
                                                    className={cn({ 'rotate-180': isOutgoing }, 'm-0', 'mr-3')}
                                                />
                                                {log.action}
                                            </span>
                                        )
                                    }

                                    return log[accessor as keyof OcppCommunicationLog] || '--'
                                }
                                return (
                                    <STableCell key={accessor} data-testid={'cell-' + accessor}>
                                        {formatCell()}
                                    </STableCell>
                                )
                            })}
                        </STableRow>
                    ))}
                </STableBody>
            </STable>

            <div ref={observerTarget} />

            {containerRef.current?.scrollTop ? (
                <ScrollToTopButton
                    onClick={() => {
                        if (containerRef) {
                            containerRef.current?.scrollTo(0, 0)
                        }
                    }}
                >
                    <SignInArrowIcon className="-rotate-90" />
                </ScrollToTopButton>
            ) : null}

            {loading && <div className="shimmer-container shimmer-small" data-testid="loading-logs"></div>}
            <STableActions>
                {actions &&
                    actions.map((action, i) => (
                        <Button
                            loading={loading}
                            key={i}
                            onClick={action.onClick}
                            label={action.title}
                            data-testid={action.title}
                        />
                    ))}
            </STableActions>
        </STableContainer>
    )
}
